import type { ReactNode } from 'react'

import { PageHeader } from './PageHeader.tsx'
import { Tabs, type TabItem } from './Tabs.tsx'

import './layout.css'

export interface ListPageProps<K extends string = string> {
  title: string
  badge?: ReactNode
  back?: ReactNode
  /**
   * 주 버튼. 자리는 이 컴포넌트가 정한다 —
   * 탭 줄이 있으면 탭 오른쪽 끝, 없으면 검색 툴바 오른쪽 끝(§1-3 · §14-1).
   */
  primaryAction?: ReactNode
  /** 탭. 2개 미만이면 탭 줄 자체가 사라진다(§1-3) */
  tabs?: readonly TabItem<K>[]
  tabValue?: K
  onTabChange?: (id: K) => void
  tabsLabel?: string
  /** 검색 입력 + 필터 알약. 원자 `Toolbar` 를 그대로 넣는다 */
  toolbar?: ReactNode
  /**
   * 툴바를 탭 줄 오른쪽 끝에 세운다 — 주 버튼은 제목 줄 오른쪽으로 올라간다(「작업 준비」 · eric 2026-10-06).
   * 탭 줄이 없으면 뜻이 없어 평소 배치로 돌아간다
   */
  toolbarInTabs?: boolean
  /**
   * **2단 탭**(하위 메뉴 · `Tabs` `variant="subnav"`) — 툴바 줄 왼쪽 맨 앞에 선다. 툴바(검색 · 필터)는 같은 줄 오른쪽(eric 2026-10-13
   * 「검색창 왼쪽에.. 지금은 필터링이 아니라 서브 메뉴잖아」 → 「왼쪽 탭 = 어디로 갈지 · 검색 옆 칩 = 무엇을 걸러 볼지」).
   * 넘기면 `toolbarInTabs` 는 뜻이 없다 — 툴바는 이 줄에 선다
   */
  subTabs?: ReactNode
  /** 표 · 상세 펼침 등 본문. 스크롤은 여기서만 생긴다(`.apage` 441행) */
  children: ReactNode
}

/**
 * 목록 화면 뼈대 — README §14-1. 대시보드·모니터링을 뺀 전 화면이 같은 문법을 쓴다.
 *
 * ```
 * .ahead   제목 (+ 상태 배지)
 * .atabs   가로 탭 + 우측 주 버튼   ← 탭이 1개면 이 줄 자체를 두지 않는다
 * .apage
 *   .atool   검색 + 필터 알약 + (탭 없으면) 오른쪽 끝 주 버튼
 *   children 표
 * ```
 *
 * `toolbarInTabs` 이면 툴바가 `.atabs` 오른쪽 끝, 주 버튼이 `.ahead` 오른쪽 끝에 서고 `.atool` 줄은 없다.
 * `subTabs`(2단 탭 · 하위 메뉴)를 넘기면 `.atool` 줄 왼쪽 맨 앞에 서고 툴바는 같은 줄 오른쪽이다(`toolbarInTabs` 는 무시).
 *
 * 🔴 **주 버튼은 한 자리에만 나온다.** 호출부가 자리를 고르지 않는다 — 두 곳에 같이 그리면
 *    §1-3 위반이라 이 컴포넌트가 배치를 쥐고 있다.
 *
 * 스크롤 영역은 `.apage` 한 곳이다. 바깥은 전부 `overflow:hidden`(§7).
 */
export function ListPage<K extends string>({
  title,
  badge,
  back,
  primaryAction,
  tabs,
  tabValue,
  onTabChange,
  tabsLabel,
  toolbar,
  toolbarInTabs = false,
  subTabs,
  children,
}: ListPageProps<K>) {
  const hasTabs =
    tabs !== undefined && tabs.length > 1 && tabValue !== undefined && onTabChange !== undefined
  const inTabs = hasTabs && toolbarInTabs && subTabs === undefined
  const showToolbarRow =
    subTabs !== undefined ||
    (toolbar !== undefined && !inTabs) ||
    (!hasTabs && primaryAction !== undefined)
  const tabActions = inTabs ? toolbar : primaryAction

  return (
    <div className="apageframe">
      <PageHeader
        title={title}
        {...(badge === undefined ? {} : { badge })}
        {...(back === undefined ? {} : { back })}
        {...(inTabs && primaryAction !== undefined ? { actions: primaryAction } : {})}
      />

      {hasTabs ? (
        <Tabs
          items={tabs}
          value={tabValue}
          onChange={onTabChange}
          label={tabsLabel ?? title}
          {...(tabActions === undefined ? {} : { actions: tabActions })}
        />
      ) : null}

      <div className="apage">
        {showToolbarRow ? (
          <div className={subTabs === undefined ? 'atool' : 'atool atool--nav'}>
            {subTabs === undefined ? null : <div className="atool__nav">{subTabs}</div>}
            {inTabs ? null : toolbar}
            {!hasTabs && primaryAction !== undefined ? (
              <div className="atool__actions">{primaryAction}</div>
            ) : null}
          </div>
        ) : null}
        {children}
      </div>
    </div>
  )
}
